import { z } from 'zod/v4';

export const AUDIT_ACCESS_REQUESTS_MAX_PAGE_SIZE = 500;
export const AUDIT_ACCESS_REQUESTS_DEFAULT_PAGE_SIZE = 15;

/** Columns `sort` accepts, as `column:ASC|DESC` pairs, comma-separated. */
export const AuditAccessRequestsSortColumns = [
  'status',
  'createdAt',
  'updatedAt',
] as const;

const sortColumnAlternatives = AuditAccessRequestsSortColumns.join('|');

/**
 * Query for `GET /api/audits/:id/access-requests` — the legacy
 * `GET /audits-service/v1/audit-access-requests/:id` params verbatim.
 *
 * `filter` is a JSON object; only `status` is honoured (a value, `null`, or an
 * array of values). Deliberately undeclared (zod strips unknown keys, as the
 * legacy AJV `removeAdditional` did): `q`, which the legacy schema accepted
 * but the query never read.
 */
export const AuditAccessRequestsQuerySchema = z.object({
  page: z.coerce.number().int().min(0).default(0),
  count: z.coerce
    .number()
    .int()
    .min(0)
    .max(AUDIT_ACCESS_REQUESTS_MAX_PAGE_SIZE)
    .default(AUDIT_ACCESS_REQUESTS_DEFAULT_PAGE_SIZE),
  sort: z
    .string()
    .regex(
      new RegExp(
        `^((${sortColumnAlternatives}):(ASC|DESC))(,((${sortColumnAlternatives}):(ASC|DESC)))*$`,
      ),
    )
    .optional(),
  filter: z.string().max(512).optional(),
});

export type AuditAccessRequestsQuery = z.infer<
  typeof AuditAccessRequestsQuerySchema
>;

/**
 * One access request on the wire. Dates are ISO strings. The frontends read
 * `requestedByCompanyId`, `requestedByCompanyName`, `status` and `createdAt`;
 * `looseObject` keeps the shape open like the legacy
 * `additionalProperties: true`. The legacy list also returned the row's actor
 * columns, which nothing read: who filed, withdrew or answered a request is in
 * `entity_history` (`AUDIT_ACCESS_REQUEST`).
 */
export const AuditAccessRequestRowSchema = z.looseObject({
  id: z.string(),
  auditId: z.string(),
  status: z.string().nullable(),
  requestedByCompanyId: z.string().nullable(),
  requestedForCompanyId: z.string().nullable(),
  /** The requester's own display (or official) name; `null` when it has none. */
  requestedByCompanyName: z.string().nullable(),
  createdAt: z.string().nullable(),
  updatedAt: z.string().nullable(),
});

export type AuditAccessRequestRow = z.infer<typeof AuditAccessRequestRowSchema>;
